import { useMemo, useState } from 'react'

import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
// import { LOCALE } from '@hhgtech/hhg-components/types'
import {
  CustomStylesType,
  TemplateProvider,
} from '@hhgtech/hhg-components/mantine'
import { LOCALE, VideoItemType } from '@hhgtech/hhg-components/types'
import { GetServerSideProps } from 'next'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { useIntl } from 'react-intl'

import { callApiNew } from 'api/axios'
import {
  formatCategory,
  formatHomepageBanner,
  formatSiteWideBanner,
  formatSpotlightsList,
} from 'api/dataTransform'
import { formatVideoItem } from 'api/dataTransform/videosPage'
import { PATHS } from 'api/paths'
import { checkMobile } from 'api/utils/validators'
import { HomeCommunityData } from 'components/pages/homepage/HomePage'
import { PageBody } from 'components/pages/homepage/pageBody'
import {
  NavigatorLayout,
  getDefaultLanguageOptions,
} from 'components/templates/navigatorLayout'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { IS_PRODUCTION, LOCALE_SPECS } from 'interfaces/constants'
import { HEALTH_TOOL_NAME } from 'interfaces/constants/healthTools'
import {
  ArticleApi,
  Category,
  CommunityItem,
  ExpertHeading,
  ExpertUserSummaryApi,
  FooterInfo,
  HealthTool,
  HomepageBannerType,
  MedicalReviewApi,
  SiteWideBanner,
  SpotlightSummary,
} from 'interfaces/types'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'

const SnowScreen = dynamic(() =>
  import('components/templates/snowScreen').then((m) => m.SnowScreen),
)

type FeaturedTopicApi = {
  posts: ArticleApi[]
  category: {
    term_id: number
    name: string
    permalink: string
    text_color: string
  }
}

type Props = {
  isMobileSsr: boolean
  trendingArticles: ArticleApi[]
  healthyLivingArticles: FeaturedTopicApi
  pregnancyArticles: FeaturedTopicApi
  drugArticles: FeaturedTopicApi
  expertsArticles: ExpertUserSummaryApi[]
  medicalReviewed: MedicalReviewApi[]
  spotlights: SpotlightSummary[]
  expertHeading: ExpertHeading
  sitewideBanner?: SiteWideBanner | null
  footerInfo?: FooterInfo
  homepageBanner?: HomepageBannerType | null
  homepageFooterBanner: NonNullable<HomepageBannerType['footer_banner']>
  dataNavMenuContent?: NavMenuContentType
  schemaData?: string
  homeCommunityData: HomeCommunityData
  homeCommunities: CommunityItem[]
  homeVideos: VideoItemType[]
  newsEventsTitle: string
  homeHealthTool: HEALTH_TOOL_NAME
  homeHasMostReadPosts: boolean
  homeCategories: Category[]
}

// Disable missing translation message as translations will be added later.
// We can add a toggle for this later when we have most translations.

// eslint-disable-next-line
if (!IS_PRODUCTION) {
  const consoleError = console.error.bind(console)
  // eslint-disable-next-line
  console.error = (message, ...args) => {
    if (
      typeof message === 'string' &&
      message.startsWith('Error: [@formatjs/intl Error')
    ) {
      return
    }
    consoleError(message, ...args)
  }
}

const Home = ({
  isMobileSsr,
  trendingArticles,
  healthyLivingArticles,
  drugArticles,
  pregnancyArticles,
  expertsArticles,
  medicalReviewed,
  spotlights,
  expertHeading,
  sitewideBanner,
  footerInfo,
  homepageBanner,
  homepageFooterBanner,
  dataNavMenuContent,
  schemaData,
  homeCommunityData,
  homeCommunities,
  homeVideos,
  newsEventsTitle,
  homeHealthTool,
  homeHasMostReadPosts,
  homeCategories,
}: Props) => {
  const [isTopBarAppears, setIsTopBarAppears] = useState(false)
  const onTopBarAppears = (b: boolean) => {
    setIsTopBarAppears(b)
  }
  const isSnowing = useMemo(() => {
    return new Date().getMonth() === 11 && new Date().getDate() >= 14
  }, [])
  const { formatMessage: f } = useIntl()

  const { locale, asPath } = useRouterExtend()

  return (
    <TemplateProvider variant={CustomStylesType.LEADGEN}>
      {locale === LOCALE.Vietnam && (
        <Head>
          <meta
            name="google-site-verification"
            content="RNbwS0-FnmZ2id7UY8o4zFgDM8qkI2ee_U_0ih1fFws"
          />
        </Head>
      )}
      {/* {locale === LOCALE.Vietnam && (
        <style>{`svg, img, p, button, a { filter: grayscale(1); }`}</style>
      )} */}
      <NavigatorLayout
        title={f({ id: 'home.seoTitle' })}
        description={f({ id: 'home.seoDescription' })}
        image={LOCALE_SPECS[locale].HOMEPAGE_IMAGE}
        onTopBarAppears={onTopBarAppears}
        SEOData={{
          schemaData,
          isIndexable: true,
        }}
        sitewideBanner={sitewideBanner}
        footerInfo={footerInfo}
        dataNavMenuContent={dataNavMenuContent}
        languageOptions={getDefaultLanguageOptions(locale, asPath)}
        isHomepage
      >
        {/* {process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'production' &&
      locale === LOCALE.Vietnam ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: 16,
          }}
        >
          <h1>APP TEST AREA</h1>
          <div
            style={{
              display: 'flex',
              gap: 16,
              padding: 8,
              border: '1px solid black',
              alignItems: 'center',
            }}
          >
            <img
              loading="lazy"
              src="/images/Weight_Tracker_QR-code.jpeg"
              alt=""
              style={{
                maxHeight: 100,
              }}
            />
            <a href="https://applink.hellobacsi.com/Wfm6h96nRDb">
              OR <b>Click to app: WEIGHT TRACKING</b>
            </a>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              padding: 8,
              border: '1px solid black',
              alignItems: 'center',
            }}
          >
            <img
              loading="lazy"
              src="/images/Chat_AI_QR-code.jpeg"
              alt=""
              style={{
                maxHeight: 100,
              }}
            />
            <a href="https://applink.hellobacsi.com/3rlX830fPDb">
              OR <b>Click to app: CHAT</b>
            </a>
          </div>
        </div>
      ) : null} */}
        <PageBody
          isMobileSsr={isMobileSsr}
          trendingArticles={trendingArticles}
          categories={
            (dataNavMenuContent?.categories?.items || []) as Category[]
          }
          healthTools={
            (dataNavMenuContent?.healthTools?.items || []) as HealthTool[]
          }
          healthyLivingArticles={healthyLivingArticles}
          drugArticles={drugArticles}
          pregnancyArticles={pregnancyArticles}
          expertsArticles={expertsArticles}
          isTopBarAppears={isTopBarAppears}
          medicalReviewed={medicalReviewed}
          spotlights={spotlights}
          expertHeading={expertHeading}
          homepageBanner={homepageBanner ?? undefined}
          homepageFooterBanner={homepageFooterBanner}
          homeCommunityData={homeCommunityData}
          homeCommunities={homeCommunities}
          homeVideos={homeVideos}
          newsEventsTitle={newsEventsTitle}
          homeHealthTool={homeHealthTool}
          homeHasMostReadPosts={homeHasMostReadPosts}
          homeCategories={homeCategories}
        />
        {isSnowing && <SnowScreen />}
      </NavigatorLayout>
    </TemplateProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { req, locale } = context

  const userAgent = `${req.headers['user-agent']}`

  const isMobile = checkMobile(userAgent)

  const results = await Promise.all([
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: {
        type: '',
      },
    }),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: {
        type: 'healthy-living',
      },
    }),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: {
        type: 'drug',
      },
    }),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: {
        type: 'family-pregnancy',
      },
    }),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: {
        type: 'experts',
      },
    }),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_MEDICAL_REVIEWED),
    callApiNew(PATHS.SPOTLIGHT.GET_ALL_SPOTLIGHT, {
      params: {
        perPage: 100,
        orderBy: 'start_date',
      },
    }),
    callApiNew(PATHS.EXPERT.GET_ALL_EXPERTS_HEADING),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_SITEWIDE_BANNER),
    getFooterDataPersist(),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_BANNER),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_FOOTER_BANNER),
    getNavMenuContentPersist(locale),
    callApiNew(PATHS.COMMON.GET_HOMEAGE_SCHEMA),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_COMMUNITY_DATA),
    callApiNew(PATHS.TOGETHER.GET_TOGETHER_COMMUNITY, undefined, 'together'),
    callApiNew(PATHS.COMMON.GET_HOME_VIDEOS),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_HEALTH_TOOL),
    callApiNew(PATHS.COMMON.GET_HOMEPAGE_POSTS, {
      params: { type: 'medical-news-events' },
    }),
    callApiNew(PATHS.ARTICLE.GET_ARTICLE_DETAIL, {
      params: { slug: 'most-read' },
    }),
    callApiNew(PATHS.CATEGORY.GET_CATEGORIES),
  ])

  const [
    trendingArticles,
    healthyLivingArticles,
    drugArticles,
    pregnancyArticles,
    expertsArticles,
    medicalReviewed,
    spotlightsRes,
    expertHeadingRes,
    sitewideBannerRes,
    footerInfoRes,
    homepageBannerRes,
    homepageFooterBannerRes,
    dataNavMenuContent,
    schemaRes,
    homeCommunityButtonRes,
    homeCommunityRes,
    homeVideoRes,
    homeHealthToolRes,
    homeNewsEventsRes,
    homeMostReadRes,
    homeCategoriesRes,
  ] = results

  const homeCommunityData: HomeCommunityData =
    homeCommunityButtonRes?.data?.result || {}
  const homeCommunities: CommunityItem[] =
    homeCommunityRes?.data?.communities || []
  const homeNewsEventsData = homeNewsEventsRes?.data
  const homeNewsEventsTitle = homeNewsEventsData?.title || ''
  const homeNewsEventsPosts = homeNewsEventsData?.posts || []

  return {
    props: {
      isMobileSsr: isMobile,
      trendingArticles: Array.isArray(trendingArticles?.data?.posts)
        ? trendingArticles.data.posts
        : [],
      healthyLivingArticles: healthyLivingArticles?.data ?? [],
      drugArticles: drugArticles?.data ?? [],
      pregnancyArticles: pregnancyArticles?.data ?? [],
      expertsArticles: expertsArticles?.data ?? [],
      medicalReviewed: medicalReviewed?.data ?? [],
      spotlights: formatSpotlightsList(spotlightsRes?.data?.articles) || [],
      expertHeading: expertHeadingRes?.data?.[0] || null,
      sitewideBanner: formatSiteWideBanner(
        sitewideBannerRes?.data?.[0],
        'home',
      ),
      footerInfo: footerInfoRes?.data ?? null,
      homepageBanner: formatHomepageBanner(homepageBannerRes?.data),
      homepageFooterBanner:
        formatHomepageBanner(homepageFooterBannerRes?.data)?.footer_banner ||
        null,
      dataNavMenuContent: dataNavMenuContent,
      schemaData: schemaRes?.data?.schema || '',
      homeCommunityData,
      homeCommunities,
      homeVideos: (homeVideoRes?.data?.videos ?? []).map(formatVideoItem),
      newsEventsTitle:
        homeNewsEventsTitle && homeNewsEventsPosts.length > 0
          ? homeNewsEventsTitle
          : '',
      homeHasMostReadPosts: !!homeMostReadRes?.data?.posts?.length,
      homeHealthTool:
        homeHealthToolRes?.data?.result?.health_tool?.template ||
        HEALTH_TOOL_NAME.BMI,
      homeCategories: (homeCategoriesRes?.data || [])
        .map(formatCategory)
        .slice(0, 12),
    },
  }
}

export default Home
